import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { ProposalCard as DSProposalCard, type ProposalDiffRow } from '../../ds'
import type { MerchantActionReceipt, ProposalAnswerAfter, ProposalAuthorityState, StoredProposal } from '../../services/api'
import type { LiveProposalView } from '../../shell/useAskThread'
import { rungName } from '../setup/rungCopy'
import { answerOutcome } from './answerOutcome'
import { PROPOSAL_COPY, proposalExpired } from './proposalPolicy'

/**
 * A change Marva PREPARED, for a person to confirm (MAR-175, Phase D decision 3).
 *
 * ⚠️ **Everything shown is server-issued** — the summary, the exact before and
 * after, the expiry. Nothing here is derived from the model's prose, and the
 * card never sees the token: it names a `receiptId` and the hook acts on it.
 *
 * Three shapes:
 * - **inert** (`live` is null): a reloaded thread's proposal, or an older one in
 *   this thread. Shown, never confirmable — "Prepared earlier". An inert
 *   `customer_answer` shows its summary ONLY: the question is transient
 *   (`display`, never stored) and the stored row carries no answer either.
 * - **live**: Confirm / Cancel, with the expiry. Past expiry it is disabled and
 *   says so; no request is made.
 * - **settled**: a receipt, "Cancelled — nothing changed.", or the server's
 *   refusal in plain words.
 *
 * Drawn by the design system's `ProposalCard` (D-33: seven states, a solid gold
 * edge while you can act, dashed when you can't, sage when done). This file
 * decides the state and the words — `proposalPolicy` stays the source of copy —
 * and the component draws them.
 */

const isAuthorityState = (v: unknown): v is ProposalAuthorityState =>
  !!v && typeof v === 'object' && typeof (v as ProposalAuthorityState).rung === 'string'
const isAnswerAfter = (v: unknown): v is ProposalAnswerAfter =>
  !!v && typeof v === 'object' && typeof (v as ProposalAnswerAfter).answerText === 'string'

const hhmm = (iso: string | undefined) => {
  if (!iso) return null
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? null : d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
}

function countdown(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000))
  const m = Math.floor(total / 60)
  const s = total % 60
  return `${m}:${String(s).padStart(2, '0')} left`
}

const exceptionsCount = (s: ProposalAuthorityState | null | undefined) =>
  s && Array.isArray(s.exceptions) ? s.exceptions.length : 0

/** "Shadow → Assisted" from a before/after pair. A missing before reads as "Not set". */
function rungTransition(before: unknown, after: unknown): string {
  const from = isAuthorityState(before) ? rungName(before.rung) : 'Not set'
  const to = isAuthorityState(after) ? rungName(after.rung) : '—'
  return `${from} → ${to}`
}

function diffRows(proposal: StoredProposal): ProposalDiffRow[] {
  if (proposal.capability === 'authority_change') {
    const before = isAuthorityState(proposal.before) ? proposal.before : null
    const after = isAuthorityState(proposal.after) ? proposal.after : null
    const b = exceptionsCount(before)
    const a = exceptionsCount(after)
    return [
      { label: 'Rung', from: before ? rungName(before.rung) : 'Not set', to: after ? rungName(after.rung) : '—' },
      {
        label: 'Exceptions',
        value: <span data-testid="proposal-exceptions">{b === a ? `${a}, unchanged` : `${b} → ${a}`}</span>,
      },
    ]
  }
  // The question is `display.question` — sent on the live response, never stored.
  const after = isAnswerAfter(proposal.after) ? proposal.after : null
  const question = typeof proposal.display?.question === 'string' ? proposal.display.question : ''
  return [
    { label: 'Question', pre: true, value: <span data-testid="proposal-question">{question}</span> },
    { label: 'Your answer', pre: true, value: <span data-testid="proposal-answer">{after?.answerText ?? ''}</span> },
  ]
}

/** "Done" — Shadow → Assisted, the receipt id, when, and where to read it in the Ledger. */
export function ReceiptCard({ receipt, fallback }: { receipt: MerchantActionReceipt; fallback: StoredProposal }) {
  const capability = receipt.capability ?? fallback.capability
  const what =
    capability === 'authority_change'
      ? rungTransition(receipt.before ?? fallback.before, receipt.after ?? fallback.after)
      : answerOutcome(receipt.result?.returnStatus)
  const at = hhmm(receipt.appliedAt ?? receipt.settledAt)
  const id = receipt.receiptId || fallback.receiptId
  return (
    <DSProposalCard
      state="applied"
      receipt={{
        what,
        id,
        at: at ?? undefined,
        link: { label: 'See it in the Ledger', to: `/ledger?decision=${encodeURIComponent(id)}`, as: Link },
      }}
    />
  )
}

export interface ProposalCardProps {
  proposal: StoredProposal
  /** The hook's scoped view — null unless THIS proposal is the live one. */
  live: LiveProposalView | null
  onConfirm: () => void
  onCancel: () => void
}

export default function ProposalCard({ proposal, live, onConfirm, onCancel }: ProposalCardProps) {
  const [now, setNow] = useState(() => Date.now())
  const outcome = live?.outcome
  const ticking = !!live && live.actionable && (!outcome || outcome.kind === 'retry')

  useEffect(() => {
    if (!ticking) return
    const id = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(id)
  }, [ticking])

  if (outcome?.kind === 'applied') return <ReceiptCard receipt={outcome.receipt} fallback={proposal} />

  const expired = proposalExpired(proposal.expiresAt, now)
  const at = hhmm(proposal.expiresAt)

  let state: 'inert' | 'live' | 'expired' | 'cancelled' | 'refused' | 'retry'
  if (!live) state = 'inert'
  else if (outcome?.kind === 'cancelled') state = 'cancelled'
  else if (outcome?.kind === 'refused') state = 'refused'
  // A pending retry outranks the clock: the press that got no clear answer may
  // have applied, so it is the server's to settle (replay, resume or 410), not ours.
  else if (outcome?.kind === 'retry') state = 'retry'
  else if (expired) state = 'expired'
  else state = 'live'

  const message =
    state === 'inert'
      ? PROPOSAL_COPY.inert
      : state === 'expired'
        ? PROPOSAL_COPY.expired
        : state === 'cancelled'
          ? PROPOSAL_COPY.cancelled
          : outcome?.kind === 'refused'
            ? `${outcome.copy}${outcome.changedNothing ? ` ${PROPOSAL_COPY.nothingChanged}` : ''}`
            : outcome?.kind === 'retry'
              ? outcome.copy
              : undefined

  return (
    <DSProposalCard
      state={state}
      summary={proposal.summary}
      diff={state !== 'inert' || proposal.capability !== 'customer_answer' ? diffRows(proposal) : []}
      expiry={
        (state === 'live' || state === 'retry') && at && !expired
          ? `Expires at ${at} · ${countdown(Date.parse(proposal.expiresAt) - now)}`
          : undefined
      }
      message={message}
      // While a press is in flight the hook reports `working` and not `actionable`:
      // Confirm shows it is busy (focus kept, clicks ignored) and Cancel is disabled.
      canAct={!!live?.actionable}
      working={!!live?.working}
      onConfirm={onConfirm}
      onCancel={onCancel}
    />
  )
}
